<template>
  <v-app>
    <v-expand-transition>
      <v-app-bar
        v-if="!notReady"
        id="mainNav"
        app
        :height="appbarHeight"
        class="white"
      >
        <v-container class="d-flex align-center">
          <div class="d-flex align-center">
            <img
              alt="Vuetify Logo"
              class="shrink mr-2 ml-6"
              contain
              src="@/assets/nctwcs/logo.png"
              transition="scale-transition"
              :width="logoWidth"
            />
          </div>
          <h1
            class="font-weight-bold primary--text flex-fill flex-fill text-center pl-4 text-truncate"
            :class="{ 'text-h4': !isMobile, 'text-h6': isMobile }"
          >
            {{ title }}
          </h1>
          <div class="d-none d-md-flex align-center">
            <span class="grey--text text--darken-1">Results Year:</span>
            <v-select
              :items="reportOptions"
              filled
              rounded
              hide-details
              dense
              class="ml-2 mr-6"
              style="max-width: 120px"
              v-model="reportSelection"
            ></v-select>
          </div>
        </v-container>
      </v-app-bar>
    </v-expand-transition>
    <v-main class="grey lighten-2">
      <v-fade-transition>
        <div v-if="!notReady">
          <v-container
            class="d-flex d-md-none justify-end align-center pt-4 px-4 pb-0"
          >
            <span class="grey--text text--darken-1">Results Year:</span>
            <v-select
              :items="reportOptions"
              solo
              rounded
              hide-details
              dense
              class="ml-2"
              style="max-width: 120px"
              v-model="reportSelection"
            ></v-select>
          </v-container>

          <response-rate
            :codeStats="codeStats"
            :reports="selection.reports"
            :principalReports="selection.principalReports"
            @toggle-report="onToggleReport"
          ></response-rate>
        </div>
      </v-fade-transition>
    </v-main>

    <v-dialog v-model="notReady" persistent width="300">
      <v-card color="white" dark>
        <v-card-text class="pt-6">
          <img
            alt=""
            aria-hidden="true"
            class="shrink mr-2 d-block m-auto"
            contain
            src="@/assets/nctwcs/logo.png"
            transition="scale-transition"
            width="100%"
          />
          <v-alert
            v-if="errorMessage"
            class="mt-8 mb-0"
            color="red"
            type="error"
            border="left"
            transition="scale-transition"
            >{{ errorMessage }}
          </v-alert>
          <div v-if="!errorMessage">
            <span class="sr-only">Loading</span>
            <v-progress-linear
              indeterminate
              color="primary"
              height="6"
              class="mb-0 mt-8"
            ></v-progress-linear>
          </div>
        </v-card-text>
        <v-card-text v-if="errorMessage">
          <v-btn block outlined color="primary" @click="refresh"
            >Try again</v-btn
          >
        </v-card-text>
      </v-card>
    </v-dialog>

    <item-analysis
      :label="activeReportLabel"
      :loading="loading"
      :show="[reportTypes.individual.name].some((x) => x === activeReport)"
      :districtId="districtId"
      :schoolId="schoolId"
      :datasets="activeDataSets"
      :useMapping="true"
      :hide="selectionHide"
      @close-report="onCloseReport"
    ></item-analysis>

    <agree-analysis
      :label="activeReportLabel"
      :loading="loading"
      :show="
        [reportTypes.agree.name, reportTypes.agreeHistory.name].some(
          (x) => x === activeReport,
        )
      "
      :districtId="districtId"
      :schoolId="schoolId"
      :datasets="activeDataSets"
      :useMapping="true"
      :hide="selectionHide"
      @close-report="onCloseReport"
    ></agree-analysis>
  </v-app>
</template>
<style lang="scss">
html {
  font-size: 1rem;
}

#mainNav {
  &,
  .v-toolbar__content,
  img {
    transition: all 200ms ease-in-out !important;
  }
}

.v-icon.v-icon.overlay-back {
  font-size: 24px !important;
}
</style>
<script>
import Vue from "vue";
import ResponseRate from "./Results.ResponseRate.vue";
import ItemAnalysis from "./Results.ItemAnalysis.vue";
import AgreeAnalysis from "./Results.AgreeAnalysis.vue";

import { aggregates, reportSelectOptions, reportTypes } from "./Constants.js";
import { questionTypes } from "../../../constants/Survey";

export default {
  components: { ResponseRate, ItemAnalysis, AgreeAnalysis },
  head: {
    title: {
      inner: "North Carolina Teacher Working Conditions Survey",
      complement: " ",
      separator: " ",
    },
  },
  data: () => ({
    notReady: true,
    pageYOffset: 0,
    shrinkThreshold: 70,
    errorMessage: null,
    districtId: null,
    schoolId: null,
    activeReport: null,
    activeDataSets: {},
    activeReportHide: [],
    useMapping: false,
    aggregates: [],
    mappedAggregates: [],
    codeStats: null,
    pendingStateChanges: 0,
    reportSelectOptions: reportSelectOptions.sort((a, b) =>
      a.text > b.text ? -1 : 1,
    ),
    reportSelection: "",
    historicalSurveys: [],
  }),
  computed: {
    reportOptions() {
      let options = this.reportSelectOptions;
      return options; //.filter((x) => x.text !== '2024')
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    appbarHeight() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 148
        : 70;
    },
    logoWidth() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 128
        : 48;
    },
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    title() {
      return "Teacher Working Conditions Survey";
    },
    survey() {
      return this.$store.state.survey;
    },
    selection() {
      let target = this.reportSelectOptions.find(
        (x) => x.value === this.reportSelection,
      );
      return target ? target : {};
    },
    selectionHide() {
      return this.selection
        ? this.selection.hide
          ? this.selection.hide.concat(this.activeReportHide)
          : this.activeReportHide
        : [];
    },
    stateCountsIndex() {
      return this.aggregates.findIndex(
        (x) =>
          x.key === aggregates.stateCounts.key && x.surveyId === this.survey.id,
      );
    },
    stateCounts() {
      return this.mappedAggregates[this.stateCountsIndex];
    },
    districtCountsIndex() {
      return this.aggregates.findIndex(
        (x) =>
          x.key === aggregates.districtCounts.key &&
          x.surveyId === this.survey.id &&
          x.groupedBy.some(
            (y) =>
              y.key == aggregates.districtCounts.groupedByKey.districtId &&
              y.value == this.districtId,
          ),
      );
    },
    districtCounts() {
      return this.mappedAggregates[this.districtCountsIndex];
    },
    entityCountsIndex() {
      return this.aggregates.findIndex(
        (x) =>
          x.key === aggregates.entityCounts.key &&
          x.surveyId === this.survey.id &&
          x.groupedBy.some(
            (y) =>
              y.key == aggregates.entityCounts.groupedByKey.entityId &&
              y.value == this.schoolId,
          ),
      );
    },
    entityCounts() {
      return this.mappedAggregates[this.entityCountsIndex];
    },
    reportTypes() {
      return reportTypes;
    },
    activeReportLabel() {
      let reportType = null;

      for (let key in this.reportTypes) {
        if (this.reportTypes[key].name === this.activeReport)
          reportType = this.reportTypes[key];
      }

      if (!reportType) return null;

      return `${this.selection.text} - ${reportType.label}`;
    },
  },
  watch: {
    errorMessage(val, prev) {
      if (val != prev && !this.notReady) {
        this.notReady = true;
        this.activeReport = null;
      }
    },
    ["$route.params.id"](val, prev) {
      if (val != prev) {
        this.reportSelection = val;
        if (this.$route.params.id != val) this.loadSurvey();
      }
    },
    reportSelection(val, prev) {
      if (val && val != prev) this.loadSurvey();
      if (this.$route.params.id != val)
        this.$router.push({
          name: "nctwcs.results.overview",
          params: { id: val },
        });
    },
  },
  methods: {
    handleScroll() {
      this.pageYOffset = window.pageYOffset;
    },
    onToggleReport({ districtId, schoolId, name, settings }) {
      this.pendingStateChanges++;

      this.districtId = districtId;
      this.schoolId = schoolId;
      this.activeReport = name;
      this.activeDataSets = {};
      this.activeReportHide = [];

      let surveyPromises = [];
      let aggPromises = [];
      let requestOrder = [];
      let rootAgg = { districtId, schoolId };

      let aggsNeeded = [rootAgg];
      let surveysNeeded = [];

      if (settings && settings.hide) {
        this.activeReportHide = settings.hide;
      }

      if (settings && settings.compareTo) {
        for (let i = 0; i < settings.compareTo.length; i++) {
          let surveyId = settings.compareTo[i].id;
          if (
            this.survey.id !== surveyId &&
            !this.historicalSurveys.some((x) => x.id === surveyId)
          ) {
            surveysNeeded.push(surveyId);
          }

          aggsNeeded.push({
            surveyId,
            ...rootAgg,
          });
        }
      }

      let districtGrouping = [
        {
          key: aggregates.districtCounts.groupedByKey.districtId,
          value: districtId,
        },
      ];

      let schoolGrouping = [
        {
          key: aggregates.entityCounts.groupedByKey.entityId,
          value: schoolId,
        },
      ];

      for (let i = 0; i < aggsNeeded.length; i++) {
        let aggNeeded = aggsNeeded[i];
        if (
          this.getAggIndex(
            aggregates.stateCounts.key,
            [],
            aggNeeded.surveyId,
          ) == -1
        ) {
          requestOrder.push({
            key: aggregates.stateCounts.key,
            groupedBy: [],
            surveyId: aggNeeded.surveyId,
          });

          aggPromises.push(
            this.$store.dispatch("survey/getAggregate", {
              key: aggregates.stateCounts.key,
              surveyId: aggNeeded.surveyId,
            }),
          );
        }

        if (districtId) {
          if (
            this.getAggIndex(
              aggregates.districtCounts.key,
              districtGrouping,
              aggNeeded.surveyId,
            ) == -1
          ) {
            requestOrder.push({
              key: aggregates.districtCounts.key,
              groupedBy: districtGrouping,
              surveyId: aggNeeded.surveyId,
            });
            aggPromises.push(
              this.$store.dispatch("survey/getAggregate", {
                key: aggregates.districtCounts.key,
                groupedBy: districtGrouping,
                surveyId: aggNeeded.surveyId,
              }),
            );
          }
        }

        if (schoolId) {
          if (
            this.getAggIndex(
              aggregates.entityCounts.key,
              schoolGrouping,
              aggNeeded.surveyId,
            ) == -1
          ) {
            requestOrder.push({
              key: aggregates.entityCounts.key,
              groupedBy: schoolGrouping,
              surveyId: aggNeeded.surveyId,
            });
            aggPromises.push(
              this.$store.dispatch("survey/getAggregate", {
                key: aggregates.entityCounts.key,
                groupedBy: schoolGrouping,
                surveyId: aggNeeded.surveyId,
              }),
            );
          }
        }
      }

      for (let i = 0; i < surveysNeeded.length; i++) {
        surveyPromises.push(
          this.$store.dispatch("survey/getSurvey", surveysNeeded[i]),
        );
      }

      // Load Survey Detail (ensures mapping can happen)
      Promise.all(surveyPromises).then(
        (surveys) => {
          surveys.forEach((survey) => {
            this.historicalSurveys.push(survey);
          });

          // Load Aggs
          Promise.all(aggPromises)
            .then(
              (results) => {
                results.forEach((aggs) => {
                  aggs.forEach((x) => {
                    this.setAggregate(x);
                  });
                });

                this.pendingStateChanges++;

                // Set State Data
                setTimeout(() => {
                  switch (name) {
                    case reportTypes.agreeHistory.name: {
                      let datasets = {};

                      // Root Comparison
                      datasets[settings.columnLabel] = this.mappedAggregates[
                        schoolId
                          ? this.entityCountsIndex
                          : this.districtId
                          ? this.districtCountsIndex
                          : this.stateCountsIndex
                      ];

                      for (let i = 0; i < settings.compareTo.length; i++) {
                        let comparison = settings.compareTo[i];
                        let aggIndex = this.getAggIndex(
                          schoolId
                            ? aggregates.entityCounts.key
                            : districtId
                            ? aggregates.districtCounts.key
                            : aggregates.stateCounts.key,
                          schoolId
                            ? schoolGrouping
                            : districtId
                            ? districtGrouping
                            : [],
                          comparison.id,
                        );

                        if (aggIndex > -1) {
                          let agg = JSON.parse(
                            JSON.stringify(this.mappedAggregates[aggIndex]),
                          );

                          let compareToConfig = settings.compareTo.find(
                            (x) => x.id === agg.surveyId,
                          );

                          if (
                            compareToConfig.hide &&
                            compareToConfig.hide.length > 0
                          ) {
                            agg.results = agg.results.filter(
                              (x) =>
                                !compareToConfig.hide.some((y) => y === x.key),
                            );
                          }

                          datasets[comparison.label] = agg;
                        }
                      }

                      this.activeDataSets = datasets;
                      this.useMapping = true;
                      break;
                    }
                    default: {
                      this.activeDataSets = {
                        state: this.stateCounts,
                        district: this.districtCounts,
                        school: this.entityCounts,
                      };

                      this.useMapping = false;
                    }
                  }

                  if (settings && settings.useAgg) {
                    for (let key in this.activeDataSets) {
                      if (
                        !this.activeDataSets[key] ||
                        !this.activeDataSets[key].results
                      )
                        continue;

                      for (
                        let i = 0;
                        i < this.activeDataSets[key].results.length;
                        i++
                      ) {
                        if (
                          !settings.useAgg(this.activeDataSets[key].results[i])
                        ) {
                          this.activeDataSets[key].results.splice(i, 1);
                          i--;
                        }
                      }
                    }
                  }

                  this.pendingStateChanges--;
                }, 200);
              },
              (error) => {
                this.errorMessage =
                  error.errorMessage ?? "An unexpected error occurred.";
              },
            )
            .finally(() => this.pendingStateChanges--);
        },
        (error) => {
          this.errorMessage =
            error.errorMessage ?? "An unexpected error occurred.";
        },
      );
    },
    setAggregate(agg) {
      let i = this.getAggIndex(agg.key, agg.groupedBy, agg.surveyId);
      if (i > -1) {
        Vue.set(this.aggregates, i, agg);
        this.setMappedAgg(i);
      } else {
        this.aggregates.push(agg);
        this.setMappedAgg(this.aggregates.length - 1);
      }
    },
    getAggIndex(key, groupedBy, surveyId) {
      let groupKey = stringifyPairArray(groupedBy);

      return this.aggregates.findIndex(
        (x) =>
          x.surveyId == (surveyId ?? this.survey.id) &&
          x.key === key &&
          stringifyPairArray(x.groupedBy) === groupKey,
      );
    },
    onCloseReport() {
      this.districtId = null;
      this.schoolId = null;
      this.activeReport = null;
    },
    refresh() {
      window.location.reload();
    },
    loadSurvey() {
      // Artificial delay to eliminate state stutter
      let minWait = Date.now() + 1000;

      this.notReady = true;
      this.aggregates = [];
      this.codeStats = null;

      this.$store.dispatch("survey/load", this.reportSelection).then(
        () => {
          Promise.all([
            this.$store.dispatch("survey/getCodeStats"),
            this.$store.dispatch("entities/load"),
          ]).then(
            (results) => {
              let next = () => {
                if (minWait < Date.now()) {
                  this.notReady = false;

                  this.codeStats = results[0].codeStats.map((x) => {
                    const entityPath = x.metaData.filter(
                      (x) => x.key === "entityPath",
                    )[0]?.value;
                    if (entityPath) {
                      const pathParts = entityPath.split(".");
                      x.metaData.push({
                        key: "DistrictId",
                        value: pathParts.length > 1 ? pathParts[0] : null,
                      });

                      x.metaData.push({
                        key: "EntityId",
                        value:
                          pathParts.length > 1 ? pathParts[1] : pathParts[0],
                      });
                    }
                    return x;
                  });
                } else setTimeout(next, 100);
              };

              next();
            },
            (error) => {
              this.errorMessage =
                error.errorMessage ?? "An unexpected error occurred.";
            },
          );
        },
        (error) => {
          this.errorMessage =
            error.errorMessage ?? "An unexpected error occurred.";
        },
      );
    },
    setMappedAgg(index) {
      let agg = JSON.parse(JSON.stringify(this.aggregates[index]));
      let surveyRef =
        this.survey.id === agg.surveyId
          ? this.survey
          : this.historicalSurveys.find((x) => x.id === agg.surveyId);

      if (!surveyRef) return;

      for (let i = 0; i < surveyRef.categories.length; i++) {
        for (let ii = 0; ii < surveyRef.categories[i].questions.length; ii++) {
          let question = surveyRef.categories[i].questions[ii];
          switch (question.type) {
            case questionTypes.table: {
              let optionMapping = question.options.filter(
                (x) => x.value != x.mappingValue,
              );
              if (
                question.key != question.mappingKey ||
                optionMapping.length > 0
              )
                remap(question.key, question.mappingKey, optionMapping);

              question.children.forEach((x) => {
                if (x.key != x.mappingKey || optionMapping.length > 0)
                  remap(x.key, x.mappingKey, optionMapping);
              });
              break;
            }
            default: {
              let optionMapping = question.options.filter(
                (x) => x.mappingValue && x.value != x.mappingValue,
              );

              if (
                question.key != question.mappingKey ||
                optionMapping.length > 0
              )
                remap(question.key, question.mappingKey, optionMapping);

              break;
            }
          }
        }
      }

      this.mappedAggregates[index] = agg;

      function remap(key, mappingKey, optionMapping) {
        let resultIndex = agg.results.findIndex((x) => x.key === key);
        if (resultIndex > -1) {
          agg.results[resultIndex].key = mappingKey;

          for (
            let optionIndex = 0;
            optionIndex < optionMapping.length;
            optionIndex++
          ) {
            let i = agg.results[resultIndex].counts.findIndex(
              (x) => x.key == optionMapping[optionIndex].value,
            );

            if (i > -1)
              agg.results[resultIndex].counts[i].key =
                optionMapping[optionIndex].mappingValue;
          }
        }
      }
    },
  },
  created() {
    window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  mounted() {
    this.pageYOffset = window.pageYOffset;

    let favicon = document.querySelector("#favicon");
    favicon.href = require("../../../assets/nctwcs/logo.png");

    // Configure App
    this.$store.commit("setTheme", "nctwcs");
    this.$store.commit("setTenantId", "NC_DPI");

    // Load Survey Data
    let option = this.reportSelectOptions.find(
      (x) => x.value === this.$route.params.id,
    );

    this.reportSelection = option
      ? option.value
      : this.reportSelectOptions.find((x) => !x.disabled).value;
  },
};

function stringifyPairArray(arr) {
  return arr
    ? arr
        .slice()
        .map((x) => `key:${x.key},value:${x.value}`)
        .sort()
        .join("|")
    : null;
}
</script>
